import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useQueue } from '../hooks/useQueue';
import Swal from 'sweetalert2';
import { FiLogOut, FiStar, FiSun, FiMoon } from 'react-icons/fi'; 

// 🚀 นำเข้าเครื่องมือแผนที่ Leaflet
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import HorizontalCalendar from '../../../components/HorizontalCalendar';
import { saveToSyncQueue, processSyncQueue } from '../../../utils/syncManager';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

const API_URL = 'https://app.thundermaxhome.com/api.php';
const UPLOAD_URL = 'https://app.thundermaxhome.com/upload.php';

// ตั้งค่าไอคอนหมุดแผนที่
const markerIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

// ดึงการคลิกแผนที่เพื่อวางหมุด
function LocationMarker({ position, setPosition }) {
  const map = useMapEvents({
    click(e) {
      setPosition(e.latlng);
      map.flyTo(e.latlng, map.getZoom());
    }
  });
  return position === null ? null : (
    <Marker position={position} icon={markerIcon} draggable={true} eventHandlers={{ dragend: (e) => setPosition(e.target.getLatLng()) }} />
  );
}

export default function QueuePage({ setActiveTab }) {
  const { user, logout } = useAuth();
  const { jobs, addNewJob, updateJobData } = useQueue(user);
  
  // 📶 ดักจับสถานะ Online / Offline
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  useEffect(() => {
    import('../../../utils/syncManager').then(({ processSyncQueue }) => {
      const handleOnline = () => { setIsOffline(false); processSyncQueue(); };
      const handleOffline = () => setIsOffline(true);
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);
      // พยายาม sync ทันทีที่เปิดแอปถ้าเน็ตมี
      if (navigator.onLine) processSyncQueue();
      return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      };
    });
  }, []);

  // 📢 ระบบ Popup แจ้งเตือนแพตช์อัปเดต (Patch Notes) โชว์แค่ครั้งเดียว
  useEffect(() => {
    const patchVersion = 'v1.1_offline_sync';
    if (!localStorage.getItem('patch_notes_' + patchVersion)) {
      Swal.fire({
        title: 'อัปเดตใหม่! (Patch 1.1)',
        html: `
          <div class="text-left text-sm space-y-2 mt-2 text-gray-700">
            <p><b>✨ สิ่งที่เพิ่มเข้ามาใหม่:</b></p>
            <ul class="list-disc pl-5">
              <li><b>ระบบออฟไลน์ (Offline Mode)</b> - บันทึกงานได้แม้ไม่มีสัญญาณเน็ต</li>
              <li><b>ระบบแจ้งเตือน (Push Notifications)</b> - เตือนงานด่วนทันที</li>
              <li><b>ระบบนำทาง (Navigation)</b> - กดปุ่มนำทางไปหน้างานผ่าน Google Maps ฟรี</li>
              <li><b>ไอคอนบริษัท</b> - เปลี่ยนไอคอนแอปเป็นโลโก้ของบริษัทแล้ว!</li>
            </ul>
          </div>
        `,
        icon: 'info',
        confirmButtonText: 'รับทราบ',
        confirmButtonColor: '#2563eb',
        allowOutsideClick: false
      }).then(() => {
        localStorage.setItem('patch_notes_' + patchVersion, 'true');
      });
    }
  }, []);

  const [commissions, setCommissions] = useState([]);

  // ดึงข้อมูลค่าคอมมิชชั่นสะสมของช่าง
  useEffect(() => {
    if (user?.uid) {
      fetch(`${API_URL}?action=get_tech_commissions&tech_uid=${user.uid}`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setCommissions(data.data || []);
          }
        })
        .catch(err => console.error("Error fetching commissions:", err));
    }
  }, [user]);

  const totalCommission = commissions.reduce((sum, item) => sum + (parseFloat(item.commission_amount) || 0), 0);

  // 🌓 จัดการเปิด/ปิด ธีมมืดและธีมสว่างในแอปช่างมือถือ
  const [isDarkMode, setIsDarkMode] = useState(
    localStorage.getItem('theme') === 'dark' || !localStorage.getItem('theme')
  );

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
      document.documentElement.style.colorScheme = 'dark';
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.add('light');
      document.documentElement.classList.remove('dark');
      document.documentElement.style.colorScheme = 'light';
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [techList, setTechList] = useState([]);
  const [viewMode, setViewMode] = useState('main'); 
  const [selectedPendingJob, setSelectedPendingJob] = useState(null);
  const [approveForm, setApproveForm] = useState({ price: '', appointmentDate: '', appointmentTime: '', technicianId: '' });
  
  const [showMap, setShowMap] = useState(false);
  const [mapPosition, setMapPosition] = useState(null);
  const [jobTypeFilter, setJobTypeFilter] = useState('all'); 

  const [newJobForm, setNewJobForm] = useState({ 
    customerName: '', phone: '', address: '', problem: '', price: '', mapLink: '', technicianId: '', appointmentDate: '', appointmentTime: '', job_type: '' 
  });

  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  const todayStr = `${yyyy}-${mm}-${dd}`;

  useEffect(() => {
    if (mapPosition) {
      setNewJobForm(prev => ({ ...prev, mapLink: `http://maps.google.com/?q=${mapPosition.lat},${mapPosition.lng}` }));
    }
  }, [mapPosition]);

  useEffect(() => {
    if (user?.role === 'owner') {
      fetch(`${API_URL}?action=get_users`)
        .then(res => res.json())
        .then(data => setTechList(data))
        .catch(err => console.error("Error fetching technicians:", err));
    }
  }, [user]);

  const pendingJobs = jobs.filter(j => j.status === 'pending_approval');
  
  const activeJobs = jobs.filter(job => {
    const isActive = job.status === 'pending' || job.status === 'traveling' || job.status === 'in_progress';
    const isTypeMatch = jobTypeFilter === 'all' || job.job_type === jobTypeFilter; 
    
    if (user?.role === 'owner') return isActive && isTypeMatch; 
    const techIds = job.technicianId ? job.technicianId.split(',') : [];
    return isActive && techIds.includes(user?.uid) && job.appointmentDate === todayStr && isTypeMatch;
  });

  // 🧮 🚀 ระบบคำนวณ Level และ XP
  const techCompletedJobsArray = jobs.filter(job => {
    const techIds = job.technicianId ? job.technicianId.split(',') : [];
    return techIds.includes(user?.uid) && job.status === 'completed';
  });
  const allUserCompletedJobs = techCompletedJobsArray.length;
  const techTodayJobs = jobs.filter(job => {
    const techIds = job.technicianId ? job.technicianId.split(',') : [];
    return techIds.includes(user?.uid) && job.appointmentDate === todayStr;
  });

  const getLevelData = (completedCount) => {
    let l = 1;
    while (true) {
      let nextLvlReq = 5 * l * (l + 1); 
      if (completedCount < nextLvlReq) {
        let currentLvlReq = 5 * (l - 1) * l;
        let percent = ((completedCount - currentLvlReq) / (nextLvlReq - currentLvlReq)) * 100;
        return { level: l, currentXP: completedCount, nextXP: nextLvlReq, percent };
      }
      l++;
    }
  };
  const levelData = getLevelData(allUserCompletedJobs);

  // ⭐️ 🚀 สมองกลคำนวณเรตติ้งดาวเฉลี่ยของช่าง
  const ratedJobs = techCompletedJobsArray.filter(job => parseFloat(job.rating) > 0);
  const averageRating = ratedJobs.length > 0 
    ? (ratedJobs.reduce((sum, job) => sum + parseFloat(job.rating), 0) / ratedJobs.length).toFixed(1)
    : '0.0';

  const handleGetLocation = () => {
    Swal.fire({ title: 'กำลังโหลดแผนที่...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => { 
          setMapPosition({ lat: pos.coords.latitude, lng: pos.coords.longitude }); 
          setShowMap(true); 
          Swal.close(); 
        },
        () => { 
          setMapPosition({ lat: 13.7649, lng: 100.5383 }); 
          setShowMap(true); 
          Swal.close(); 
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } else {
      setMapPosition({ lat: 13.7649, lng: 100.5383 }); 
      setShowMap(true); 
      Swal.close();
    }
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!newJobForm.technicianId) {
      Swal.fire('แจ้งเตือน', 'กรุณาเลือกช่างผู้รับผิดชอบอย่างน้อย 1 คน', 'warning');
      return;
    }
    Swal.fire({ title: 'กำลังบันทึก...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    try {
      let assignedTechName = 'ไม่ระบุ';
      let assignedTechPhone = '-';
      let assignedTechId = newJobForm.technicianId;

      const ids = assignedTechId.split(',');
      const names = [];
      const phones = [];
      
      ids.forEach(id => {
        if (id === user?.uid) {
          names.push(user?.name || 'เจ้าของร้าน');
          phones.push(user?.phone || '-');
        } else {
          const t = techList.find(tech => tech.uid === id);
          if (t) {
            names.push(t.name);
            phones.push(t.phone || '-');
          }
        }
      });

      if (names.length > 0) assignedTechName = names.join(', ');
      if (phones.length > 0) assignedTechPhone = phones.join(', ');

      await addNewJob({ 
        ...newJobForm, 
        technicianId: assignedTechId, 
        technicianName: assignedTechName, 
        technicianPhone: assignedTechPhone, 
        status: 'pending' 
      });

      setIsAddModalOpen(false);
      setNewJobForm({ customerName: '', phone: '', address: '', problem: '', price: '', mapLink: '', technicianId: '', appointmentDate: '', appointmentTime: '', job_type: '' });
      setShowMap(false); 
      setMapPosition(null);
      Swal.fire({ icon: 'success', title: 'สร้างคิวสำเร็จ!', showConfirmButton: false, timer: 1500 });
    } catch (error) { 
      Swal.fire('ข้อผิดพลาด', error.message, 'error');
    }
  };

  const handleApproveJob = async (e) => {
    e.preventDefault();
    if (!approveForm.technicianId) {
      Swal.fire('แจ้งเตือน', 'กรุณาเลือกช่างผู้รับผิดชอบอย่างน้อย 1 คน', 'warning');
      return;
    }
    Swal.fire({ title: 'กำลังอนุมัติงาน...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    try {
      let assignedTechName = 'ไม่ระบุ';
      let assignedTechPhone = '-';
      
      const ids = approveForm.technicianId.split(',');
      const names = [];
      const phones = [];
      
      ids.forEach(id => {
        if (id === user?.uid) {
          names.push(user?.name || 'เจ้าของร้าน');
          phones.push(user?.phone || '-');
        } else {
          const t = techList.find(tech => tech.uid === id);
          if (t) {
            names.push(t.name);
            phones.push(t.phone || '-');
          }
        }
      });
      
      if (names.length > 0) assignedTechName = names.join(', ');
      if (phones.length > 0) assignedTechPhone = phones.join(', ');
      
      await updateJobData(selectedPendingJob.id, { 
        ...approveForm, 
        technicianName: assignedTechName, 
        technicianPhone: assignedTechPhone, 
        status: 'pending' 
      });

      Swal.fire({ icon: 'success', title: 'จ่ายงานสำเร็จ!', showConfirmButton: false, timer: 1500 });
      setSelectedPendingJob(null);
    } catch (error) { 
      Swal.fire('ข้อผิดพลาด', error.message, 'error'); 
    }
  };

  const handleCancelPendingJob = async () => {
    Swal.fire({ 
      title: 'ยืนยันการยกเลิกงาน?', 
      icon: 'warning', 
      showCancelButton: true, 
      confirmButtonColor: '#ef4444', 
      confirmButtonText: 'ใช่, ปฏิเสธ', 
      cancelButtonText: 'กลับไปตรวจสอบ' 
    }).then(async (result) => {
      if (result.isConfirmed) {
        Swal.fire({ title: 'กำลังบันทึก...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
        try {
          await updateJobData(selectedPendingJob.id, { status: 'cancelled' });
          Swal.fire({ icon: 'success', title: 'ยกเลิกคิวงานเรียบร้อยแล้ว!', showConfirmButton: false, timer: 1500 });
          setSelectedPendingJob(null);
        } catch (error) { 
          Swal.fire('ข้อผิดพลาด', error.message, 'error'); 
        }
      }
    });
  };

  const handleStatusChange = async (job, newStatus) => {
    setSelectedJob(null);
    Swal.fire({ title: 'กำลังอัปเดต...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    try {
      await updateJobData(job.id, { status: newStatus });
      Swal.fire({ icon: 'success', title: 'อัปเดตสำเร็จ!', timer: 1000, showConfirmButton: false });
    } catch (error) { 
      Swal.fire('ข้อผิดพลาด', error.message, 'error'); 
    }
  };

  const handleSuggestExtraWork = async (job) => {
    const { value: text } = await Swal.fire({ 
      title: 'รายงานหน้างาน', 
      input: 'textarea', 
      showCancelButton: true, 
      confirmButtonText: 'ส่งแจ้งแอดมิน', 
      cancelButtonText: 'ยกเลิก' 
    });
    if (text) {
      if (!navigator.onLine) {
        saveToSyncQueue('update_job', { id: job.id, fieldReport: text, isReportPending: 1 });
        setSelectedJob(prev => ({...prev, fieldReport: text, isReportPending: 1}));
        Swal.fire('บันทึกออฟไลน์แล้ว!', 'ระบบจะส่งรายงานเมื่อมีสัญญาณเน็ต', 'info');
      } else {
        Swal.fire({ title: 'กำลังส่งข้อมูล...', didOpen: () => Swal.showLoading() });
        await updateJobData(job.id, { fieldReport: text, isReportPending: 1 });
        setSelectedJob(prev => ({...prev, fieldReport: text, isReportPending: 1}));
        Swal.fire('ส่งรายงานแล้ว!', 'โปรดรอแอดมินปรับราคาครับ', 'success');
      }
    }
  };

  const handleEditJobByOwner = async (job) => {
    const { value: formValues } = await Swal.fire({
      title: '✏️ ปรับปรุงราคา',
      html: `
        <textarea id="swal-prob" class="swal2-textarea" style="margin-top:0;">${job.problem}</textarea>
        <input id="swal-price" type="number" class="swal2-input" value="${job.price}">
      `,
      showCancelButton: true, 
      confirmButtonText: 'บันทึกอัปเดต', 
      preConfirm: () => ({ 
        problem: document.getElementById('swal-prob').value, 
        price: document.getElementById('swal-price').value 
      })
    });
    if (formValues) {
      Swal.fire({ title: 'กำลังอัปเดต...', didOpen: () => Swal.showLoading() });
      await updateJobData(job.id, { problem: formValues.problem, price: formValues.price, isReportPending: 0 });
      setSelectedJob(prev => ({...prev, problem: formValues.problem, price: formValues.price, isReportPending: 0}));
      Swal.fire('อัปเดตสำเร็จ!', '', 'success');
    }
  };

  const compressImage = (file, maxWidth = 1920, maxHeight = 1920, quality = 0.7) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const newFile = new File([blob], file.name.replace(/\.[^/.]+$/, "") + ".jpg", {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                resolve(newFile);
              } else {
                reject(new Error('Canvas to Blob failed'));
              }
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = (error) => reject(error);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleImageUpload = async (e, type) => {
    let file = e.target.files[0]; 
    if (!file) return;
    
    Swal.fire({ title: 'กำลังบีบอัดและอัปโหลด...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    try {
      if (file.type.startsWith('image/')) {
        try {
          file = await compressImage(file);
        } catch (compressErr) {
          console.warn("Compression failed, using original file", compressErr);
        }
      }

      if (!navigator.onLine) {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = () => {
          const base64data = reader.result;
          saveToSyncQueue('upload_image', { jobId: selectedJob.id, type, base64: base64data });
          setSelectedJob(prev => ({ ...prev, [type]: base64data }));
          Swal.fire('บันทึกรูปออฟไลน์!', 'จะทำการอัปโหลดเมื่อมีสัญญาณเน็ต', 'info');
        };
        return;
      }

      const formData = new FormData(); 
      formData.append('image', file);
      
      const res = await fetch(UPLOAD_URL, { method: 'POST', body: formData });
      const result = await res.json();
      
      if (result.success) {
        await updateJobData(selectedJob.id, { [type]: result.url });
        setSelectedJob(prev => ({ ...prev, [type]: result.url }));
        Swal.fire({ icon: 'success', title: 'อัปโหลดสำเร็จ!', showConfirmButton: false, timer: 1500 });
      } else { 
        throw new Error(result.message); 
      }
    } catch (error) { 
      Swal.fire('ข้อผิดพลาด', error.message, 'error'); 
    }
  };

  const confirmAction = (t, txt, btn, action) => {
    Swal.fire({ 
      title: t, text: txt, icon: 'question', showCancelButton: true, confirmButtonText: btn, cancelButtonText: 'ยกเลิก' 
    }).then((res) => { 
      if (res.isConfirmed) action(); 
    });
  };

  const getStatusBadge = (status, isPending) => {
    if (isPending == 1) return <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700 animate-pulse">รอแอดมินแก้ราคา</span>;
    switch(status) {
      case 'pending': return <span className="px-3 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-700">รอเดินทาง</span>;
      case 'traveling': return <span className="px-3 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-700">กำลังเดินทาง</span>;
      case 'in_progress': return <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700">กำลังทำ</span>;
      default: return null;
    }
  };

  const getJobTypeBadge = (type) => {
    switch(type) {
      case 'aircon': return <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-lg text-[10px] font-bold whitespace-nowrap">❄️ แอร์</span>;
      case 'electric': return <span className="bg-yellow-50 text-yellow-700 px-3 py-1 rounded-lg text-[10px] font-bold whitespace-nowrap">⚡ ไฟฟ้า</span>;
      case 'contractor': return <span className="bg-purple-50 text-purple-700 px-3 py-1 rounded-lg text-[10px] font-bold whitespace-nowrap">🧱 รับเหมา</span>;
      case 'solar': return <span className="bg-orange-50 text-orange-700 px-3 py-1 rounded-lg text-[10px] font-bold whitespace-nowrap">☀️ โซล่าเซลล์</span>;
      case 'door': return <span className="bg-teal-50 text-teal-700 px-3 py-1 rounded-lg text-[10px] font-bold whitespace-nowrap">🚪 ประตูอัตโนมัติ</span>;
      case 'evertor': return <span className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded-lg text-[10px] font-bold whitespace-nowrap">🛗 ลิฟท์</span>;
      case 'camera': return <span className="bg-rose-50 text-rose-700 px-3 py-1 rounded-lg text-[10px] font-bold whitespace-nowrap">📹 กล้องวงจรปิด</span>;
      case 'rework': return <span className="bg-red-50 text-red-700 px-3 py-1 rounded-lg text-[10px] font-bold whitespace-nowrap">🔧 งานแก้</span>;
      default: return <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-lg text-[10px] font-bold whitespace-nowrap">ทั่วไป</span>;
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-[#0f172a] flex justify-center transition-colors duration-300">
      <div className="w-full max-w-md bg-gray-50 dark:bg-[#131825] flex flex-col relative shadow-2xl h-[100dvh] overflow-hidden transition-colors duration-300">
        
        {/* 🚀 รูปแบบ Profile + Level Card ใหม่สไตล์เกมเมอร์ (รองรับ Light/Dark Mode) 🚀 */}
        <div className="bg-white dark:bg-[#0f172a] rounded-b-[2rem] p-5 pt-8 text-slate-800 dark:text-white border-b border-gray-200 dark:border-none shadow-sm dark:shadow-xl relative shrink-0 z-10 transition-colors duration-300">
          <div className="absolute top-6 right-5 flex items-center gap-2">
            {/* 🌗 ปุ่มสลับ Dark/Light Mode */}
            <button 
              onClick={() => setIsDarkMode(!isDarkMode)} 
              className="p-2 text-slate-500 dark:text-gray-400 hover:text-yellow-500 dark:hover:text-yellow-400 transition-colors bg-slate-100 dark:bg-[#1e293b] rounded-full shadow-md border border-slate-200 dark:border-none"
              title="สลับธีม"
            >
              {isDarkMode ? <FiSun size={14} /> : <FiMoon size={14} />}
            </button>
            <button onClick={logout} className="text-slate-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors p-2 bg-slate-100 dark:bg-[#1e293b] rounded-full shadow-md border border-slate-200 dark:border-none" title="ออกจากระบบ">
              <FiLogOut size={14} />
            </button>
          </div>

          <div className="flex items-center gap-3 mb-4">
            <div className="relative">
              <img 
                src={user?.profileImage || `https://ui-avatars.com/api/?name=${user?.name || 'User'}&background=2563eb&color=fff`} 
                alt="Profile" 
                className="w-14 h-14 rounded-full border-2 border-blue-500 object-cover shadow-sm bg-gray-800" 
              />
              <div className="absolute top-0 right-0 w-3 h-3 bg-green-500 border-2 border-white dark:border-[#0f172a] rounded-full"></div>
            </div>
            <div>
              {/* 🚀 เพิ่ม Rating ดาวเฉลี่ย + ค่าคอม เข้ามาข้างๆ Level 🚀 */}
              <div className="flex flex-wrap items-center gap-1.5 mb-0.5 font-bold">
                <span className="text-yellow-500 dark:text-yellow-400 flex items-center gap-0.5 text-[11px] font-black">
                  <FiStar className="fill-current" /> {averageRating}
                </span>
                <span className="bg-gradient-to-r from-blue-500 to-blue-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded shadow-sm">
                  Lv.{levelData.level}
                </span>
                <span className="text-[9px] font-bold text-pink-600 dark:text-pink-400 bg-pink-500/10 px-1.5 py-0.5 rounded">
                  {user?.role === 'owner' ? 'Admin Master' : 'Technician'}
                </span>
                {/* 💰 แสดงยอดค่าคอม ตรงนี้! 💰 */}
                <span className="text-[9px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded">
                  💰 ค่าคอม: ฿{totalCommission.toLocaleString()}
                </span>
              </div>
              <h2 className="text-lg font-bold leading-tight text-slate-800 dark:text-white">{user?.name || 'ช่างเทคนิค'}</h2>
              <p className="text-[9px] text-slate-500 dark:text-gray-400 font-mono mt-0.5">ID: {user?.username || '---'}</p>
            </div>
          </div>

          <div className="bg-slate-50 dark:bg-[#1e293b] rounded-xl p-3 border border-slate-200 dark:border-[#334155] shadow-inner transition-colors duration-300">
            <div className="flex justify-between text-[10px] font-bold text-slate-500 dark:text-gray-300 mb-1.5">
              <span className="text-blue-600 dark:text-blue-400">Level {levelData.level}</span>
              <span className="text-slate-400 dark:text-gray-500">Level {levelData.level + 1}</span>
            </div>
            <div className="h-2 bg-slate-200 dark:bg-[#0f172a] rounded-full overflow-hidden mb-1.5 relative">
              <div className="h-full bg-gradient-to-r from-blue-600 to-cyan-400 rounded-full transition-all duration-1000" style={{ width: `${levelData.percent}%` }}></div>
            </div>
            <p className="text-[9px] text-center text-slate-500 dark:text-gray-400">
              สำเร็จอีก <span className="text-cyan-600 dark:text-cyan-400 font-black">{levelData.nextXP - levelData.currentXP}</span> งาน เพื่อเลื่อนระดับ
            </p>
          </div>
        </div>

        {/* เมนูแท็บสำหรับ Owner */}
        {user?.role === 'owner' && (
          <div className="flex bg-gray-200 dark:bg-slate-800 p-1 mx-4 mt-4 rounded-xl shrink-0 shadow-sm transition-colors">
            <button onClick={() => setViewMode('main')} className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${viewMode === 'main' ? 'bg-white dark:bg-[#1e293b] shadow text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-450'}`}>คิวงาน ({activeJobs.length})</button>
            <button onClick={() => setViewMode('pending')} className={`flex-1 py-1.5 text-xs font-bold rounded-lg relative transition-all ${viewMode === 'pending' ? 'bg-white dark:bg-[#1e293b] shadow text-orange-600 dark:text-orange-400' : 'text-gray-500 dark:text-gray-450'}`}>
              รอรับจอง ({pendingJobs.length})
              {pendingJobs.length > 0 && <span className="absolute top-1.5 right-4 w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span>}
            </button>
          </div>
        )}
        
        {/* Dropdown กรองประเภทงาน */}
        <div className="px-4 mb-2 mt-3 shrink-0">
          <select value={jobTypeFilter} onChange={e => setJobTypeFilter(e.target.value)} className="w-full p-2.5 bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-bold text-gray-700 dark:text-gray-200 shadow-sm outline-none transition-colors">
            <option value="all">🔍 ดูหมวดหมู่งานทั้งหมด</option>
            <option value="aircon">❄️ งานติดตั้ง/ล้างแอร์</option>
            <option value="electric">⚡ งานระบบไฟฟ้า</option>
            <option value="contractor">🧱 งานรับเหมาทั่วไป</option>
            <option value="solar">☀️ โซล่าเซลล์</option>
            <option value="door">🚪 ประตูอัตโนมัติ</option>
            <option value="evertor">🛗 ลิฟท์</option>
            <option value="camera">📹 กล้องวงจรปิด</option>
            <option value="rework">🔧 งานแก้ (รับประกัน)</option>
          </select>
        </div>

        {/* โซนการ์ดแสดงรายการคิวงาน */}
        <div className="flex-1 overflow-y-auto p-4 pb-24 space-y-4">
          {viewMode === 'main' && activeJobs.length > 0 ? (
            activeJobs.map((job) => (
              <div key={job.id} className="bg-white dark:bg-[#1e293b] p-4 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 relative transition-colors">
                <div className={`absolute left-0 top-0 bottom-0 w-1 ${job.isReportPending == 1 ? 'bg-red-500 animate-pulse' : 'bg-orange-500'}`}></div>
                <div className="flex justify-between items-start mb-2">
                  <h2 className="font-bold text-base text-gray-800 dark:text-white truncate">{job.customerName}</h2>
                  {getStatusBadge(job.status, job.isReportPending)}
                </div>
                <div className="mb-2">{getJobTypeBadge(job.job_type)}</div>
                <p className="text-gray-650 dark:text-gray-305 text-xs mb-3 truncate">อาการ: {job.problem}</p>
                <div className="flex justify-between items-center border-t border-gray-100 dark:border-gray-700 pt-3">
                  <span className="text-xs font-medium text-blue-600 dark:text-blue-400">{user?.role === 'owner' ? `฿${job.price}` : 'รายละเอียด ➡️'}</span>
                  <button onClick={() => setSelectedJob(job)} className="bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400 px-3 py-1.5 rounded-lg text-xs font-bold">ดู/แก้ไข</button>
                </div>
              </div>
            ))
          ) : viewMode === 'main' ? (
            <div className="text-center py-10 bg-white dark:bg-[#1e293b] rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm text-gray-400 dark:text-gray-500 transition-colors">
              <p className="text-3xl mb-2">📭</p>
              <p className="font-bold text-xs">ไม่มีคิวงานที่ต้องซ่อมในขณะนี้</p>
            </div>
          ) : null}

          {viewMode === 'pending' && user?.role === 'owner' && pendingJobs.map((job) => (
            <div key={job.id} className="bg-white dark:bg-[#1e293b] p-4 rounded-2xl shadow-sm border border-red-200 dark:border-red-900/30 transition-colors">
               <h2 className="font-bold text-base text-gray-800 dark:text-white mb-2">{job.customerName}</h2>
               <div className="mb-2">{getJobTypeBadge(job.job_type)}</div>
               <p className="text-gray-600 dark:text-gray-300 text-xs mb-4">อาการ: {job.problem}</p>
               <button onClick={() => { setSelectedPendingJob(job); setApproveForm({price:'', appointmentDate:'', appointmentTime:'', technicianId:''}); }} className="w-full bg-orange-100 text-orange-700 px-3 py-2.5 rounded-lg text-xs font-bold">ตรวจสอบ & จัดการคิว</button>
            </div>
          ))}
        </div>

        {viewMode === 'main' && user?.role === 'owner' && (
          <button onClick={() => setIsAddModalOpen(true)} className="absolute bottom-20 right-5 w-12 h-12 bg-blue-600 text-white rounded-full shadow-lg text-2xl flex items-center justify-center z-30">+</button>
        )}

        {/* ----------------------------------------------------- */}
        {/* Modal: เพิ่มคิวงานใหม่ */}
        {/* ----------------------------------------------------- */}
        {isAddModalOpen && (
          <div className="absolute inset-0 z-50 bg-black/60 flex justify-center items-center p-4">
            <div className="bg-white dark:bg-[#1e293b] text-gray-800 dark:text-white w-full rounded-2xl p-5 relative overflow-y-auto max-h-[90%] shadow-2xl border dark:border-gray-700">
              <button onClick={() => setIsAddModalOpen(false)} className="absolute top-3 right-4 text-gray-400 text-xl font-bold">×</button>
              <h1 className="text-base font-bold mb-4 text-gray-800 dark:text-white">เพิ่มคิวงานใหม่</h1>
              
              <form onSubmit={handleAddSubmit} className="space-y-3">
                <input required type="text" placeholder="ชื่อลูกค้า" className="w-full bg-white dark:bg-[#0f172a] text-gray-850 dark:text-white border border-gray-300 dark:border-[#334155] p-2.5 rounded-xl outline-none text-sm font-semibold" value={newJobForm.customerName} onChange={e => setNewJobForm({...newJobForm, customerName: e.target.value})} />
                <input required type="tel" placeholder="เบอร์โทรศัพท์" className="w-full bg-white dark:bg-[#0f172a] text-gray-850 dark:text-white border border-gray-300 dark:border-[#334155] p-2.5 rounded-xl outline-none text-sm font-semibold" value={newJobForm.phone} onChange={e => setNewJobForm({...newJobForm, phone: e.target.value})} />
                
                <select required className="w-full bg-white dark:bg-[#0f172a] text-gray-855 dark:text-white border border-gray-300 dark:border-[#334155] p-2.5 rounded-xl outline-none text-sm font-bold" value={newJobForm.job_type} onChange={e => setNewJobForm({...newJobForm, job_type: e.target.value})}>
                  <option value="">-- เลือกประเภทงาน --</option>
                  <option value="aircon">งานติดตั้ง/ล้างแอร์</option>
                  <option value="electric">งานระบบไฟฟ้า</option>
                  <option value="contractor">งานรับเหมาทั่วไป</option>
                  <option value="solar">โซล่าเซลล์</option>
                  <option value="door">ประตูอัตโนมัติ</option>
                  <option value="evertor">ลิฟท์</option>
                  <option value="camera">กล้องวงจรปิด</option>
                  <option value="rework">งานแก้ (รับประกัน)</option>
                </select>

                <textarea required placeholder="ที่อยู่ลูกค้า" className="w-full bg-white dark:bg-[#0f172a] text-gray-855 dark:text-white border border-gray-300 dark:border-[#334155] p-2.5 rounded-xl outline-none text-sm" rows="2" value={newJobForm.address} onChange={e => setNewJobForm({...newJobForm, address: e.target.value})}></textarea>
                
                <div className="bg-blue-50 dark:bg-blue-955/20 p-2.5 rounded-xl border border-blue-100 dark:border-blue-900/30">
                  <label className="block text-[10px] font-bold text-blue-800 dark:text-blue-400 mb-1.5">พิกัดแผนที่</label>
                  {!showMap ? (
                    <button type="button" onClick={handleGetLocation} className="w-full bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-405 border-2 border-dashed border-blue-300 dark:border-blue-800 py-2 rounded-lg text-xs font-bold">📍 ระบุพิกัดแผนที่</button>
                  ) : (
                    <div>
                      <div className="h-32 rounded-lg overflow-hidden border-2 border-blue-400 mb-2">
                        <MapContainer center={mapPosition} zoom={16} style={{ height: '100%', width: '100%' }}>
                          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                          <LocationMarker position={mapPosition} setPosition={setMapPosition} />
                        </MapContainer>
                      </div>
                      <button type="button" onClick={handleGetLocation} className="w-full bg-gray-200 dark:bg-slate-700 text-xs py-1.5 rounded-lg font-bold text-gray-800 dark:text-white">รีเซ็ตพิกัด</button>
                    </div>
                  )}
                </div>

                <textarea required placeholder="อาการเสีย" className="w-full bg-white dark:bg-[#0f172a] text-gray-855 dark:text-white border border-gray-300 dark:border-[#334155] p-2.5 rounded-xl outline-none text-sm" rows="2" value={newJobForm.problem} onChange={e => setNewJobForm({...newJobForm, problem: e.target.value})}></textarea>
                
                {/* 🚀 เลือกช่างเป็นทีมช่างหลายคน (Checkboxes) */}
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold text-gray-500 dark:text-gray-400 mb-1">เลือกทีมช่างผู้รับผิดชอบ (เลือกได้หลายคน)</label>
                  <div className="grid grid-cols-2 gap-2 bg-white dark:bg-[#0f172a] p-3 rounded-xl border border-gray-300 dark:border-[#334155] max-h-36 overflow-y-auto custom-scrollbar">
                    {/* เจ้าของร้าน/แอดมินคนล็อกอิน */}
                    <label className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-gray-150 dark:hover:bg-[#1e293b] rounded transition text-xs select-none">
                      <input 
                        type="checkbox"
                        checked={newJobForm.technicianId ? newJobForm.technicianId.split(',').includes(user?.uid) : false}
                        onChange={() => {
                          const selectedIds = newJobForm.technicianId ? newJobForm.technicianId.split(',') : [];
                          let newIds = [...selectedIds];
                          if (selectedIds.includes(user?.uid)) {
                            newIds = newIds.filter(id => id !== user?.uid);
                          } else {
                            newIds.push(user?.uid);
                          }
                          setNewJobForm({ ...newJobForm, technicianId: newIds.join(',') });
                        }}
                        className="w-3.5 h-3.5 text-blue-600 rounded cursor-pointer"
                      />
                      <span className="font-bold text-blue-600 dark:text-blue-400">{user?.name} (รับงานเอง)</span>
                    </label>

                    {techList.map(t => {
                      const selectedIds = newJobForm.technicianId ? newJobForm.technicianId.split(',') : [];
                      const isChecked = selectedIds.includes(t.uid);
                      return (
                        <label key={t.uid} className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-gray-150 dark:hover:bg-[#1e293b] rounded transition text-xs select-none">
                          <input 
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {
                              let newIds = [...selectedIds];
                              if (isChecked) {
                                newIds = newIds.filter(id => id !== t.uid);
                              } else {
                                newIds.push(t.uid);
                              }
                              setNewJobForm({ ...newJobForm, technicianId: newIds.join(',') });
                            }}
                            className="w-3.5 h-3.5 text-blue-600 rounded cursor-pointer"
                          />
                          <span className="font-semibold text-gray-700 dark:text-gray-300">{t.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
                
                <input required type="number" placeholder="ราคาประเมิน (บาท)" className="w-full bg-white dark:bg-[#0f172a] text-gray-855 dark:text-white border border-gray-300 dark:border-[#334155] p-2.5 rounded-xl outline-none text-sm" value={newJobForm.price} onChange={e => setNewJobForm({...newJobForm, price: e.target.value})} />
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 dark:text-gray-400 mb-1">📅 เลือกวันที่นัดหมาย</label>
                    <HorizontalCalendar 
                      selectedDate={newJobForm.appointmentDate} 
                      onChange={dateStr => setNewJobForm({...newJobForm, appointmentDate: dateStr})} 
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 dark:text-gray-400 mb-1">⏰ เลือกช่วงเวลาบริการ</label>
                    <select required className="w-full bg-white dark:bg-[#0f172a] text-gray-855 dark:text-white border border-gray-300 dark:border-[#334155] p-3 rounded-xl text-sm font-bold outline-none" value={newJobForm.appointmentTime} onChange={e => setNewJobForm({...newJobForm, appointmentTime: e.target.value})}>
                      <option value="">-- เลือกเวลา --</option>
                      <option value="09:00">ช่วงเช้า (09:00 - 12:00 น.)</option>
                      <option value="13:00">ช่วงบ่าย (13:00 - 16:00 น.)</option>
                      <option value="16:30">ช่วงเย็น (16:30 - 18:30 น.)</option>
                    </select>
                  </div>
                </div>
                
                <button type="submit" className="w-full bg-blue-600 text-white font-bold py-3 rounded-xl mt-2 text-sm shadow-md">บันทึก & จ่ายงาน</button>
              </form>
            </div>
          </div>
        )}

        {/* ----------------------------------------------------- */}
        {/* Modal: อนุมัติและจ่ายงาน */}
        {/* ----------------------------------------------------- */}
        {selectedPendingJob && user?.role === 'owner' && (
          <div className="absolute inset-0 z-50 bg-black/60 flex justify-center items-center p-4">
            <div className="bg-white dark:bg-[#1e293b] text-gray-800 dark:text-white w-full rounded-2xl p-5 relative max-h-[90%] overflow-y-auto border dark:border-gray-700">
              <button onClick={() => setSelectedPendingJob(null)} className="absolute top-3 right-4 text-gray-400 font-bold text-xl">×</button>
              <h1 className="text-base font-black text-blue-800 dark:text-blue-400 mb-3">อนุมัติและจ่ายงาน</h1>
              
              <div className="bg-gray-50 dark:bg-[#0f172a] border border-gray-200 dark:border-[#334155] rounded-xl p-3 mb-4 space-y-3 transition-colors">
                 <p className="text-[10px] text-gray-500 dark:text-gray-450 font-bold">ลูกค้า: <span className="text-xs text-gray-800 dark:text-gray-200 font-bold">{selectedPendingJob.customerName}</span></p>
                 <p className="text-[10px] text-gray-500 dark:text-gray-455 font-bold">เบอร์: <span className="text-xs text-blue-600 dark:text-blue-400 font-bold">{selectedPendingJob.phone || '-'}</span></p>
                 <div className="mt-1">{getJobTypeBadge(selectedPendingJob.job_type)}</div>
                 <p className="text-[10px] text-gray-500 dark:text-gray-455 font-bold">อาการ: <span className="text-xs text-red-600 dark:text-red-400 font-black">{selectedPendingJob.problem}</span></p>
              </div>

              <form onSubmit={handleApproveJob} className="space-y-3">
                {/* 🚀 เลือกช่างเป็นทีมช่างหลายคน (Checkboxes) */}
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold text-gray-500 dark:text-gray-455 mb-1">เลือกทีมช่างผู้รับผิดชอบ (เลือกได้หลายคน)</label>
                  <div className="grid grid-cols-2 gap-2 bg-white dark:bg-[#0f172a] p-3 rounded-xl border border-gray-300 dark:border-[#334155] max-h-36 overflow-y-auto custom-scrollbar">
                    {/* เจ้าของร้าน/แอดมินคนล็อกอิน */}
                    <label className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-gray-150 dark:hover:bg-[#1e293b] rounded transition text-xs select-none">
                      <input 
                        type="checkbox"
                        checked={approveForm.technicianId ? approveForm.technicianId.split(',').includes(user?.uid) : false}
                        onChange={() => {
                          const selectedIds = approveForm.technicianId ? approveForm.technicianId.split(',') : [];
                          let newIds = [...selectedIds];
                          if (selectedIds.includes(user?.uid)) {
                            newIds = newIds.filter(id => id !== user?.uid);
                          } else {
                            newIds.push(user?.uid);
                          }
                          setApproveForm({ ...approveForm, technicianId: newIds.join(',') });
                        }}
                        className="w-3.5 h-3.5 text-blue-600 rounded cursor-pointer"
                      />
                      <span className="font-bold text-blue-600 dark:text-blue-400">{user?.name} (รับงานเอง)</span>
                    </label>

                    {techList.map(t => {
                      const selectedIds = approveForm.technicianId ? approveForm.technicianId.split(',') : [];
                      const isChecked = selectedIds.includes(t.uid);
                      return (
                        <label key={t.uid} className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-gray-150 dark:hover:bg-[#1e293b] rounded transition text-xs select-none">
                          <input 
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {
                              let newIds = [...selectedIds];
                              if (isChecked) {
                                newIds = newIds.filter(id => id !== t.uid);
                              } else {
                                newIds.push(t.uid);
                              }
                              setApproveForm({ ...approveForm, technicianId: newIds.join(',') });
                            }}
                            className="w-3.5 h-3.5 text-blue-600 rounded cursor-pointer"
                          />
                          <span className="font-semibold text-gray-700 dark:text-gray-300">{t.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
                
                <input required type="number" placeholder="ราคา (บาท)" className="w-full bg-white dark:bg-[#0f172a] text-gray-800 dark:text-white border border-gray-300 dark:border-[#334155] p-2.5 rounded-xl text-sm outline-none" onChange={e => setApproveForm({...approveForm, price: e.target.value})} />
                
                <div className="grid grid-cols-2 gap-2">
                  <input required type="date" className="w-full bg-white dark:bg-[#0f172a] text-gray-850 dark:text-white border border-gray-300 dark:border-[#334155] p-2.5 rounded-xl text-sm outline-none" onChange={e => setApproveForm({...approveForm, appointmentDate: e.target.value})} />
                  <input required type="time" className="w-full bg-white dark:bg-[#0f172a] text-gray-855 dark:text-white border border-gray-300 dark:border-[#334155] p-2.5 rounded-xl text-sm outline-none" onChange={e => setApproveForm({...approveForm, appointmentTime: e.target.value})} />
                </div>
                
                <div className="grid grid-cols-2 gap-2 pt-2">
                  <button type="button" onClick={handleCancelPendingJob} className="bg-red-100 text-red-700 font-bold py-2.5 rounded-xl text-sm">ปฏิเสธคิว</button>
                  <button type="submit" className="bg-orange-500 text-white font-bold py-2.5 rounded-xl text-sm shadow-md">จ่ายงาน</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ----------------------------------------------------- */}
        {/* Modal: รายละเอียดงาน */}
        {/* ----------------------------------------------------- */}
        {selectedJob && (
          <div className="absolute inset-0 z-50 bg-black/60 flex justify-center items-end sm:items-center">
            <div className="bg-white dark:bg-[#1e293b] text-gray-800 dark:text-white w-full h-[85%] sm:h-auto sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 flex flex-col relative border dark:border-gray-700">
              <button onClick={() => setSelectedJob(null)} className="absolute top-3 right-4 text-gray-400 text-2xl font-bold">×</button>
              
              <div className="flex justify-between items-center mb-3 pr-6">
                <h2 className="text-lg font-black text-gray-800 dark:text-white truncate">{selectedJob.customerName}</h2>
                {user?.role === 'owner' && (
                  <button onClick={() => handleEditJobByOwner(selectedJob)} className="bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-400 text-[9px] px-2 py-1 rounded font-bold">✏️ แก้ไข</button>
                )}
              </div>
              
              <div className="space-y-3 flex-1 overflow-y-auto pb-4">
                {selectedJob.fieldReport && (
                  <div className={`p-3 rounded-lg border ${selectedJob.isReportPending == 1 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                    <p className="text-[10px] font-bold text-red-600">🚨 รายงานช่าง</p>
                    <p className="text-xs">{selectedJob.fieldReport}</p>
                  </div>
                )}
                
                <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-[9px] text-gray-500 font-bold">ประเภท</p>
                      <div className="mt-1">{getJobTypeBadge(selectedJob.job_type)}</div>
                    </div>
                    <div>
                      <p className="text-[9px] text-gray-500 font-bold">ที่อยู่</p>
                      <p className="text-xs mt-0.5">{selectedJob.address}</p>
                      {selectedJob.mapLink && (
                        <button 
                          onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${selectedJob.mapLink}`, '_blank')}
                          className="mt-2 bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] px-3 py-1.5 rounded-lg font-bold shadow-sm flex items-center justify-center gap-1 w-full transition-colors"
                        >
                          🚗 นำทางไปหน้างาน (Google Maps)
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="border-t pt-2">
                    <p className="text-[9px] text-gray-500 font-bold">รายละเอียด</p>
                    <p className="text-xs font-bold mt-0.5">{selectedJob.problem}</p>
                  </div>
                </div>
                
                {(user?.role === 'owner' || selectedJob.status === 'completed') && (
                  <div className="bg-green-50 p-3 rounded-xl border border-green-100">
                    <p className="text-[9px] text-green-600 font-bold">ราคาสุทธิ</p>
                    <p className="font-bold text-green-700 text-base">฿{selectedJob.price}</p>
                  </div>
                )}
                
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {['beforeImage', 'duringImage', 'afterImage'].map((type, i) => {
                    const titles = ['ก่อนทำ', 'ระหว่างทำ', 'หลังทำ'];
                    return (
                      <div key={type} className="bg-gray-50 p-2 rounded-lg text-center border border-dashed border-gray-300">
                        <p className="text-[9px] text-gray-500 mb-1">{titles[i]}</p>
                        {selectedJob[type] ? (
                          <img src={selectedJob[type]} alt={type} className="w-full h-12 object-cover rounded shadow-sm" />
                        ) : (
                          <label className={`cursor-pointer bg-blue-50 text-blue-700 text-[9px] px-2 py-1 rounded block font-bold`}>
                            อัปโหลด<input type="file" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, type)} />
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mt-2 border-t pt-3">
                {user?.role !== 'owner' && selectedJob.status !== 'completed' && (
                  <button onClick={() => handleSuggestExtraWork(selectedJob)} className="col-span-2 bg-red-50 text-red-600 border border-red-200 font-bold py-2.5 rounded-xl text-xs">📝 รายงานหน้างาน</button>
                )}
                <a href={`tel:${selectedJob.phone}`} className="bg-gray-200 text-gray-700 font-bold py-2.5 rounded-xl flex items-center justify-center text-xs">📞 โทร</a>
                
                {selectedJob.isReportPending == 1 ? (
                  <div className="bg-red-100 text-red-600 font-bold py-2.5 rounded-xl flex items-center justify-center text-[10px]">⏳ รอปรับราคา</div>
                ) : (
                  <>
                    {selectedJob.status === 'pending' && <button onClick={() => confirmAction('เดินทาง?', '', '🚗 ยืนยัน', () => handleStatusChange(selectedJob, 'traveling'))} className="bg-purple-500 text-white font-bold rounded-xl text-xs shadow">🚗 เดินทาง</button>}
                    {selectedJob.status === 'traveling' && <button onClick={() => confirmAction('เริ่มงาน?', '', '🛠️ เริ่ม', () => handleStatusChange(selectedJob, 'in_progress'))} className="bg-blue-600 text-white font-bold rounded-xl text-xs shadow">🛠️ เริ่มงาน</button>}
                    {selectedJob.status === 'in_progress' && <button onClick={() => confirmAction('จบงาน?', '', '✅ จบ', () => handleStatusChange(selectedJob, 'completed'))} className="bg-green-500 text-white font-bold rounded-xl text-xs shadow">✅ จบงาน</button>}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="absolute bottom-0 w-full bg-white dark:bg-[#1e293b] border-t border-gray-200 dark:border-gray-700 flex justify-around py-2.5 pb-5 px-2 z-20 transition-colors">
          <button onClick={() => setActiveTab('queue')} className="flex flex-col items-center text-blue-600"><span className="text-xl mb-0.5">🏠</span><span className="text-[10px] font-bold">คิวงาน</span></button>
          <button onClick={() => setActiveTab('history')} className="flex flex-col items-center text-gray-400"><span className="text-xl mb-0.5">📊</span><span className="text-[10px] font-bold">ประวัติ</span></button>
          <button onClick={() => setActiveTab('profile')} className="flex flex-col items-center text-gray-400"><span className="text-xl mb-0.5">👤</span><span className="text-[10px] font-bold">โปรไฟล์</span></button>
        </div>
      </div>
    </div>
  );
}